/**
 * ★★ I18N-01 — LE SÉLECTEUR DE LANGUE DES PAGES PUBLIQUES.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * OÙ IL VA, ET OÙ IL NE VA PAS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Il appartient aux pages qu'un VISITEUR lit : la vitrine, la boutique, le
 * portail de l'abonné, les écrans de connexion.
 *
 * ⚠️ **Pas dans le back-office.** L'arbitrage du 04/09 a écarté la langue par
 *    utilisateur : le personnel d'une maison travaille dans la langue de la
 *    maison, réglée une fois dans la configuration. Poser ce sélecteur dans la
 *    barre du back-office laisserait croire à un réglage par compte qui
 *    n'existe pas, et deux guichetiers finiraient par se décrire des écrans
 *    différents au téléphone.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * TROIS PARTIS PRIS D'ÉCRITURE
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  1. **Les noms de langue ne sont jamais traduits** — « Deutsch », pas
 *     « allemand ». Quelqu'un qui cherche l'allemand ne lit pas le français :
 *     lui présenter le nom dans la langue qu'il ne comprend pas est le défaut
 *     classique de ces sélecteurs. C'est `NOM_LANGUE` de `@tb/shared`.
 *
 *  2. **Aucun drapeau.** Un drapeau suisse ne dirait pas laquelle des quatre
 *     langues nationales on sert, et un drapeau allemand dirait le mauvais
 *     pays à un lecteur bernois. Une langue n'est pas un pays.
 *
 *  3. **`<select>` natif, pas une liste déroulante maison.** Trois options ne
 *     valent pas un composant à ouvrir, piéger le focus et fermer à
 *     l'échappement — et le natif est le seul qui fonctionne au clavier, au
 *     lecteur d'écran et sur mobile sans qu'on ait à le prouver.
 */

import { CODE_LANGUE, LANGUES, NOM_LANGUE, type Langue } from '@tb/shared'

import { cn } from '../lib/cn'
import { choisirLangue, useLangue, useLangueVitrine } from '../lib/langue'

/**
 * Le libellé accessible du sélecteur, dans les trois langues.
 *
 * ★ Il se traduit, lui, contrairement aux noms de langue : c'est une phrase de
 *   l'interface, adressée à quelqu'un qui lit déjà la langue courante.
 */
const LIBELLE: Readonly<Record<Langue, string>> = {
  fr: 'Langue',
  de: 'Sprache',
  en: 'Language',
}

export interface SelecteurLangueProps {
  /** `compact` n'affiche que « FR / DE / EN » — pour un en-tête serré. */
  readonly taille?: 'compact' | 'complet'
  readonly className?: string | undefined
}

/**
 * ★★ DEUX COMPOSANTS, PAS UN DRAPEAU — et surtout pas un ternaire sur un hook.
 *
 * La première version écrivait `vitrine ? useLangueVitrine() : useLangue()`.
 * TypeScript l'acceptait, et c'était pourtant un défaut : un appel de hook
 * conditionnel désaligne l'ordre des hooks si le drapeau change entre deux
 * rendus, et React lit alors l'état d'un autre hook. Ici le drapeau ne change
 * jamais en pratique — ce qui est exactement ce qui rend ce genre de faute
 * indétectable jusqu'au jour où elle devient vraie.
 *
 * Les deux composants partagent tout leur rendu ; seule la SOURCE de la langue
 * diffère, et chacun n'appelle qu'un seul hook, toujours le même.
 */

/** Le sélecteur d'une surface de théâtre — boutique, portail, connexion. */
export function SelecteurLangue(props: SelecteurLangueProps) {
  return <Selecteur {...props} langue={useLangue()} />
}

/** Le sélecteur de la vitrine, qui ne dépend d'aucun théâtre. */
export function SelecteurLangueVitrine(props: SelecteurLangueProps) {
  return <Selecteur {...props} langue={useLangueVitrine()} />
}

function Selecteur({
  langue,
  taille = 'compact',
  className,
}: SelecteurLangueProps & { readonly langue: Langue }) {
  const etiquettes = taille === 'compact' ? CODE_LANGUE : NOM_LANGUE

  return (
    <label className={cn('inline-flex items-center gap-1.5', className)}>
      <span className="sr-only">{LIBELLE[langue]}</span>
      <select
        value={langue}
        onChange={(evenement) => {
          choisirLangue(evenement.target.value as Langue)
        }}
        aria-label={LIBELLE[langue]}
        className={cn(
          'cursor-pointer rounded-md border border-current/20 bg-transparent py-1 pl-2 pr-1',
          'text-sm font-medium text-current',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current',
        )}
      >
        {LANGUES.map((code) => (
          /* ★ `NOM_LANGUE` en `title` même en mode compact : « DE » seul ne
             dit rien à qui ne connaît pas les codes ISO, et le survol comme le
             lecteur d'écran donnent alors « Deutsch ». */
          <option key={code} value={code} title={NOM_LANGUE[code]} className="text-slate-900">
            {etiquettes[code]}
          </option>
        ))}
      </select>
    </label>
  )
}
